import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import SlidingPane from 'react-sliding-pane';
import ActivityPane from 'components/activities/ActivityPane';
import EntityFieldPane from 'components/entities/fields/FieldPane';
import ObjectivePane from 'components/objectives/ObjectivePane';
import OrganizationMemberPane from 'components/organizations/members/MemberPane';
import ProjectPane from 'components/projects/ProjectPane';
import TaskPane from 'components/tasks/TaskPane';
import TemplateFieldPane from 'components/templates/fields/FieldPane';
import UserPane from 'components/users/UserPane';
import { ADMIN_MENU_WIDTH, MAIN_SIDEBAR_WIDTH, PAGE_CONTENT_GAP } from 'components/UI/layout';
import Sidebar from 'components/UI/Sidebar';
import { FEATURES } from 'services/features/catalog';
import { useFeatureEnabled } from 'services/features/hooks';
import { clearPaneContent, getPaneContent } from 'services/pane/slice';
import {
  IActivityPane,
  IEntityFieldPane,
  IObjectivePane,
  IOrganizationMemberPane,
  ITaskPane,
  ITemplateFieldPane,
  IUserPane,
} from 'services/pane/types';
import 'react-sliding-pane/dist/react-sliding-pane.css';
import styles from './BasePage.module.css';

const BasePage = ({
  children,
  showAdminMenu = false,
}: {
  children?: React.ReactNode;
  showAdminMenu?: boolean;
}) => {
  const dispatch = useDispatch();
  const { enabled: objectivesEnabled } = useFeatureEnabled(FEATURES.objectives);
  const { enabled: tasksEnabled } = useFeatureEnabled(FEATURES.tasks);
  const paneContent = useSelector(getPaneContent);
  const showPane = !!paneContent
    && (paneContent.type !== 'TASK' || tasksEnabled)
    && (paneContent.type !== 'OBJECTIVE' || objectivesEnabled);
  let pane = <div />;
  if (paneContent && paneContent.type === 'ACTIVITY') {
    const activityPaneContent = paneContent as IActivityPane;
    const { activity } = activityPaneContent;
    pane = <ActivityPane
      activity={activity}
    />;
  /*
  else if (paneContent && paneContent.type === 'ITEM') {
    pane = <ItemPane
      defaultTab={paneContent && paneContent.id === 'NEW' && 'infos'}
      id={paneContent && paneContent.id}
    />;
  */
  } else if (paneContent && paneContent.type === 'ENTITY_FIELD') {
    const entityFieldPaneContent = paneContent as IEntityFieldPane;
    pane = <EntityFieldPane
      entityId={entityFieldPaneContent && entityFieldPaneContent.entityId}
      id={entityFieldPaneContent && entityFieldPaneContent.id}
    />;
  } else if (objectivesEnabled && paneContent && paneContent.type === 'OBJECTIVE') {
    const objectivePaneContent = paneContent as IObjectivePane;
    pane = <ObjectivePane
      defaultTab={objectivePaneContent.id === 'NEW' ? 'INFOS' : undefined}
      id={objectivePaneContent.id}
      projectId={objectivePaneContent.projectId}
    />;
  } else if (paneContent && paneContent.type === 'ORGANIZATION_MEMBER') {
    const organizationMemberPaneContent = paneContent as IOrganizationMemberPane;
    pane = <OrganizationMemberPane
      organizationId={organizationMemberPaneContent && organizationMemberPaneContent.organizationId}
      id={organizationMemberPaneContent && organizationMemberPaneContent.id}
    />;
  } else if (paneContent && paneContent.type === 'PROJECT') {
    pane = <ProjectPane
      defaultTab={paneContent && paneContent.id === 'NEW' && 'infos'}
      id={paneContent && paneContent.id}
    />;
  } else if (tasksEnabled && paneContent && paneContent.type === 'TASK') {
    const taskPaneContent = paneContent as ITaskPane;
    pane = <TaskPane
      assignee={taskPaneContent.assignee}
      dueAt={taskPaneContent.dueAt}
      id={taskPaneContent.id}
      projectId={taskPaneContent.projectId}
    />;
  } else if (paneContent && paneContent.type === 'TEMPLATE_FIELD') {
    const templateFieldPaneContent = paneContent as ITemplateFieldPane;
    pane = <TemplateFieldPane
      templateId={templateFieldPaneContent && templateFieldPaneContent.templateId}
      id={templateFieldPaneContent && templateFieldPaneContent.id}
    />;
  } else if (paneContent && paneContent.type === 'USER') {
    const userPaneContent = paneContent as IUserPane;
    pane = <UserPane id={userPaneContent.id} />;
  }

  return (
    <>
      <Sidebar />
      <SlidingPane
        isOpen={showPane}
        onRequestClose={() => dispatch(clearPaneContent())}
        overlayClassName={styles.slidingPaneOverlay}
      >
        { pane }
      </SlidingPane>
      <div
        className={showAdminMenu ? `${styles.page} ${styles.pageWithAdminMenu}` : styles.page}
        style={showAdminMenu ? {
          '--admin-menu-width': `${ADMIN_MENU_WIDTH}px`,
          '--main-sidebar-width': `${MAIN_SIDEBAR_WIDTH}px`,
          '--page-content-gap': `${PAGE_CONTENT_GAP}px`,
        } as React.CSSProperties : undefined}
      >
        { children }
      </div>
    </>
  );
};

export default BasePage;
